// Właściwości fizyczne materiałów używanych w PlexiSystem
// Wszystkie wartości są rzeczywiste, z kart technicznych producentów

export interface MaterialProperties {
  name: string;
  displayName: string;
  density: number; // kg/m³
  modulusOfElasticity: number; // MPa (Moduł Younga)
  tensileStrength: number; // MPa (wytrzymałość na rozciąganie)
  flexuralStrength: number; // MPa (wytrzymałość na zginanie)
  compressiveStrength: number; // MPa (wytrzymałość na ściskanie)
  shearStrength: number; // MPa (wytrzymałość na ścinanie)
  poissonsRatio: number; // współczynnik Poissona
  maxServiceTemp: number; // °C
  thermalExpansion: number; // 10⁻⁶/K
  impactStrength: number; // kJ/m²
  uvResistance: boolean;
  chemicalResistance: 'low' | 'medium' | 'high';
  pricePerKg: number; // PLN/kg orientacyjna cena
}

export const MATERIALS: Record<string, MaterialProperties> = {
  // PMMA - Pleksi (Polymethyl methacrylate)
  pmma_xt: {
    name: 'pmma_xt',
    displayName: 'Plexi XT (wytłaczane)',
    density: 1190,
    modulusOfElasticity: 3300,
    tensileStrength: 72,
    flexuralStrength: 115,
    compressiveStrength: 110,
    shearStrength: 65,
    poissonsRatio: 0.37,
    maxServiceTemp: 80,
    thermalExpansion: 70,
    impactStrength: 15,
    uvResistance: true,
    chemicalResistance: 'medium',
    pricePerKg: 25
  },
  
  pmma_gs: {
    name: 'pmma_gs',
    displayName: 'Plexi GS (lane)',
    density: 1190,
    modulusOfElasticity: 3200,
    tensileStrength: 80,
    flexuralStrength: 120,
    compressiveStrength: 120,
    shearStrength: 70,
    poissonsRatio: 0.37,
    maxServiceTemp: 85,
    thermalExpansion: 67,
    impactStrength: 17,
    uvResistance: true,
    chemicalResistance: 'medium',
    pricePerKg: 30
  },

  // PETG - Polyethylene terephthalate glycol
  petg: {
    name: 'petg',
    displayName: 'PET-G',
    density: 1270,
    modulusOfElasticity: 2200,
    tensileStrength: 50,
    flexuralStrength: 70,
    compressiveStrength: 65,
    shearStrength: 45,
    poissonsRatio: 0.38,
    maxServiceTemp: 70,
    thermalExpansion: 68,
    impactStrength: 35, // bardzo odporny na uderzenia
    uvResistance: false,
    chemicalResistance: 'high',
    pricePerKg: 22
  },

  // PC - Poliwęglan
  polycarbonate: {
    name: 'polycarbonate',
    displayName: 'Poliwęglan',
    density: 1200,
    modulusOfElasticity: 2400,
    tensileStrength: 65,
    flexuralStrength: 95,
    compressiveStrength: 85,
    shearStrength: 55,
    poissonsRatio: 0.37,
    maxServiceTemp: 120,
    thermalExpansion: 66,
    impactStrength: 60, // wyjątkowo odporny
    uvResistance: true,
    chemicalResistance: 'low',
    pricePerKg: 35
  },

  // HIPS - High Impact Polystyrene
  hips: {
    name: 'hips',
    displayName: 'HIPS',
    density: 1050,
    modulusOfElasticity: 2100,
    tensileStrength: 22,
    flexuralStrength: 35,
    compressiveStrength: 35,
    shearStrength: 20,
    poissonsRatio: 0.35,
    maxServiceTemp: 70,
    thermalExpansion: 80,
    impactStrength: 10,
    uvResistance: false,
    chemicalResistance: 'medium',
    pricePerKg: 15
  },

  // PCV spienione
  pvc_foam_3mm: {
    name: 'pvc_foam_3mm',
    displayName: 'PCV spienione 3mm',
    density: 500,
    modulusOfElasticity: 600,
    tensileStrength: 12,
    flexuralStrength: 15,
    compressiveStrength: 8,
    shearStrength: 6,
    poissonsRatio: 0.30,
    maxServiceTemp: 60,
    thermalExpansion: 70,
    impactStrength: 5,
    uvResistance: true,
    chemicalResistance: 'high',
    pricePerKg: 18
  },

  pvc_foam_5mm: {
    name: 'pvc_foam_5mm',
    displayName: 'PCV spienione 5mm',
    density: 500,
    modulusOfElasticity: 600,
    tensileStrength: 12,
    flexuralStrength: 15,
    compressiveStrength: 8,
    shearStrength: 6,
    poissonsRatio: 0.30,
    maxServiceTemp: 60,
    thermalExpansion: 70,
    impactStrength: 5,
    uvResistance: true,
    chemicalResistance: 'high',
    pricePerKg: 18
  },

  pvc_foam_10mm: {
    name: 'pvc_foam_10mm',
    displayName: 'PCV spienione 10mm',
    density: 450,
    modulusOfElasticity: 500,
    tensileStrength: 10,
    flexuralStrength: 12,
    compressiveStrength: 6,
    shearStrength: 5,
    poissonsRatio: 0.30,
    maxServiceTemp: 60,
    thermalExpansion: 70,
    impactStrength: 4,
    uvResistance: true,
    chemicalResistance: 'high',
    pricePerKg: 16
  },

  // Dibond
  dibond_3mm: {
    name: 'dibond_3mm',
    displayName: 'Dibond 3mm',
    density: 1800, // kompozyt aluminium + PE
    modulusOfElasticity: 26000, // głównie od aluminium
    tensileStrength: 120,
    flexuralStrength: 150,
    compressiveStrength: 140,
    shearStrength: 80,
    poissonsRatio: 0.33,
    maxServiceTemp: 80,
    thermalExpansion: 24,
    impactStrength: 25,
    uvResistance: true,
    chemicalResistance: 'high',
    pricePerKg: 45
  },

  // Szkło
  glass_4mm: {
    name: 'glass_4mm',
    displayName: 'Szkło float 4mm',
    density: 2500,
    modulusOfElasticity: 70000,
    tensileStrength: 45,
    flexuralStrength: 120,
    compressiveStrength: 1000,
    shearStrength: 30,
    poissonsRatio: 0.22,
    maxServiceTemp: 200,
    thermalExpansion: 9,
    impactStrength: 2, // kruche
    uvResistance: true,
    chemicalResistance: 'high',
    pricePerKg: 10
  },

  glass_tempered_4mm: {
    name: 'glass_tempered_4mm',
    displayName: 'Szkło hartowane 4mm',
    density: 2500,
    modulusOfElasticity: 70000,
    tensileStrength: 120, // znacznie mocniejsze
    flexuralStrength: 200,
    compressiveStrength: 1000,
    shearStrength: 60,
    poissonsRatio: 0.22,
    maxServiceTemp: 250,
    thermalExpansion: 9,
    impactStrength: 10,
    uvResistance: true,
    chemicalResistance: 'high',
    pricePerKg: 25
  },

  // Drewno (dla porównania)
  mdf_18mm: {
    name: 'mdf_18mm',
    displayName: 'MDF 18mm',
    density: 750,
    modulusOfElasticity: 4000,
    tensileStrength: 18,
    flexuralStrength: 23,
    compressiveStrength: 15,
    shearStrength: 8,
    poissonsRatio: 0.25,
    maxServiceTemp: 80,
    thermalExpansion: 12,
    impactStrength: 8,
    uvResistance: false,
    chemicalResistance: 'low',
    pricePerKg: 3
  },

  // Aluminium (profile)
  aluminum_6061: {
    name: 'aluminum_6061',
    displayName: 'Aluminium 6061',
    density: 2700,
    modulusOfElasticity: 69000,
    tensileStrength: 310,
    flexuralStrength: 280,
    compressiveStrength: 280,
    shearStrength: 185,
    poissonsRatio: 0.33,
    maxServiceTemp: 200,
    thermalExpansion: 23.6,
    impactStrength: 30,
    uvResistance: true,
    chemicalResistance: 'medium',
    pricePerKg: 20
  }
};

// Grubości standardowe dla materiałów (mm)
export const STANDARD_THICKNESSES: Record<string, number[]> = {
  pmma_xt: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30],
  pmma_gs: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40],
  petg: [1, 2, 3, 4, 5, 6, 8, 10, 12],
  polycarbonate: [2, 3, 4, 5, 6, 8, 10, 12, 15],
  hips: [1, 1.5, 2, 3, 4, 5],
  pvc_foam_3mm: [3],
  pvc_foam_5mm: [5],
  pvc_foam_10mm: [10],
  dibond_3mm: [3],
  glass_4mm: [4],
  glass_tempered_4mm: [4, 5, 6, 8, 10, 12],
  mdf_18mm: [3, 6, 10, 12, 16, 18, 22, 25],
  aluminum_6061: [1, 2, 3, 4, 5, 6, 8, 10]
};

// Współczynniki bezpieczeństwa dla różnych zastosowań
export const SAFETY_FACTORS = {
  display: 2.0, // ekspozycja, lekkie obciążenia
  storage: 3.0, // przechowywanie, półki
  structural: 4.0, // elementy konstrukcyjne
  public: 5.0, // przestrzenie publiczne
  industrial: 6.0 // zastosowania przemysłowe
};

// Typy podparcia
export type SupportType = 
  | 'simply_supported' // podparte na dwóch końcach
  | 'cantilever' // wspornik
  | 'fixed_both' // utwierdzone na obu końcach
  | 'three_sides' // podparte na 3 bokach
  | 'four_sides' // podparte na 4 bokach
  | 'point_supports'; // podparcie punktowe

// Współczynniki dla różnych typów podparcia
export const SUPPORT_COEFFICIENTS = {
  simply_supported: 0.125, // dla ugięcia belki
  cantilever: 0.125 * 8, // wspornik - 8x większe ugięcie
  fixed_both: 0.0208, // utwierdzone - mniejsze ugięcie
  three_sides: 0.04, // płyta podparta na 3 bokach
  four_sides: 0.028, // płyta podparta na 4 bokach
  point_supports: 0.25 // podparcie punktowe
};

// Funkcja pomocnicza - pobierz materiał po nazwie
export const getMaterial = (name: string): MaterialProperties | null => {
  return MATERIALS[name] || null;
};

// Funkcja pomocnicza - znajdź najlepszy materiał dla wymagań
export const findBestMaterial = (
  maxLoad: number,
  dimensions: { width: number; height: number },
  maxDeflection?: number
): string[] => {
  const suitable: string[] = [];
  
  Object.entries(MATERIALS).forEach(([key, material]) => {
    // Uproszczone sprawdzenie - do rozbudowania w kalkulatorze
    const stress = (maxLoad * 9.81) / (dimensions.width * dimensions.height / 1000000);
    if (stress < material.flexuralStrength / SAFETY_FACTORS.display) {
      suitable.push(key);
    }
  });
  
  return suitable.sort((a, b) => MATERIALS[a].pricePerKg - MATERIALS[b].pricePerKg);
};
